import React from "react";
import { Box, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AirlineSeatReclineNormalOutlinedIcon from "@mui/icons-material/AirlineSeatReclineNormalOutlined";
import FlightOutlinedIcon from "@mui/icons-material/FlightOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { borderWidth, palette, typography } from "theme/tokens";
import { fmtYmd } from "components/bookingServicing/modifyRequestParts";
import { kindOf } from "components/bookingServicing/requestKinds";
import { flightPaxTypes } from "./flightQuote";

/*
  The FLIGHT request flow's own parts: what a flight request asks to change
  (date / cabin), its row labels. Everything product-neutral — formatting,
  status pills, quote pills, chains and rounds, the stepper — is the hotel
  flow's, used as-is and never edited from here (one copy: CI duplication).
*/
export {
  fmtYmd,
  fmtDateTime,
  fmtMoney,
  StatusPill,
  NewQuotePill,
  quoteNet,
  QuoteImpact,
  groupModifyRequestChains,
  toChain,
  ChainImpact,
  chainRoundLabel,
  ModifyRequestStepper,
} from "components/bookingServicing/modifyRequestParts";

const changeRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexWrap: "wrap",
  fontSize: typography.fontSize[12],
  color: palette.neutral[670],
};

/** A flight's date + times: "27 Jan 2027 08:40–09:38". */
export const flightWhen = (d) =>
  [fmtYmd(d?.date), [d?.departureTime, d?.arrivalTime].filter(Boolean).join("–")].filter(Boolean).join(" ");

/** "PREMIUM_ECONOMY" / "Economy" → "Premium Economy" / "Economy". */
export const cabinLabel = (v) => String(v || "").toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** What a leg asks for: a new date / a new cabin. A leg always carries its
 *  booked date and cabin (`from`); only a `to` is a change. */
export const asksDate = (f) => !!f?.dates?.to;
export const asksCabin = (f) => !!f?.cabin?.to;

/** "DEL → AGR · ND 3335" — a flight in a request. */
export const flightTitle = (f) => [f.route, f.flightNumber].filter(Boolean).join(" · ") || "Flight";

/** The leg as booked, off the booking (`booking.sectors`), or null without one. */
const bookedLeg = (booking, f) => (booking?.sectors || []).find((s) => s.id === f?.sectorId) || null;

/** "Onward · DXB → BLR · 6E 1486" — with the booked leg's direction when known. */
const legTitle = (f, s) => [s && (s.direction === "return" ? "Return" : "Onward"), flightTitle(f)].filter(Boolean).join(" · ");

/** The booked leg: airline · date departure–arrival · duration · cabin · PNR. */
function BookedLeg({ leg }) {
  if (!leg) return null;
  const pnr = leg.airlinePnr || leg.supplierPnr;
  return (
    <Box sx={{ ...changeRowSx, mb: 0.5 }}>
      <FlightOutlinedIcon sx={{ fontSize: typography.fontSize[14], color: palette.neutral[470] }} />
      <span>
        {[
          leg.airlineName,
          flightWhen({ date: leg.date, departureTime: leg.departureTime, arrivalTime: leg.arrivalTime }),
          leg.flightTime,
          cabinLabel(leg.cabinClass),
          pnr && `PNR ${pnr}`,
        ]
          .filter(Boolean)
          .join(" · ")}
      </span>
    </Box>
  );
}

/** "2 Adult · 1 Child" — the booking's flight travellers. */
function Travellers({ booking }) {
  const types = flightPaxTypes(booking);
  if (!types.length) return null;
  return (
    <Box sx={{ ...changeRowSx, mb: 1 }}>
      <PeopleOutlineIcon sx={{ fontSize: typography.fontSize[14], color: palette.neutral[470] }} />
      <span>{types.map((t) => `${t.count} ${t.type}`).join(" · ")}</span>
    </Box>
  );
}

/** Two legs or more sit side by side (onward left, return right) from sm up. */
const legColumns = (flights) => ((flights || []).length > 1 ? "1fr 1fr" : "1fr");

/** A leg's cell: a rule between the legs — above it when stacked, left of the
 *  right-hand column side by side. */
const legCellSx = (i) => ({
  ...(i > 0 && { borderTop: { xs: `${borderWidth.thin} solid ${palette.neutral[70]}`, sm: "none" }, pt: { xs: 1, sm: 0 } }),
  ...(i % 2 === 1 && { borderLeft: { sm: `${borderWidth.thin} solid ${palette.neutral[70]}` }, pl: { sm: 3 } }),
  ...(i > 1 && { borderTop: { sm: `${borderWidth.thin} solid ${palette.neutral[70]}` }, pt: { sm: 1 } }),
});

/** What a request asks to change, per flight: date & time old → new, cabin old → new. */
export function RequestChanges({ flights, booking }) {
  return (
    <Box sx={{ display: "grid", gap: 1, columnGap: 3, gridTemplateColumns: { xs: "1fr", sm: legColumns(flights) } }}>
      {(flights || []).map((f, i) => (
        <Box key={`${f.sectorId || "flight"}-${i}`} sx={legCellSx(i)}>
          <Typography
            sx={{
              fontSize: typography.fontSize[13],
              fontWeight: typography.fontWeight.semiBold,
              color: palette.neutral[870],
              mb: 0.5,
            }}
          >
            {legTitle(f, bookedLeg(booking, f))}
          </Typography>
          <BookedLeg leg={bookedLeg(booking, f)} />
          {asksDate(f) && (
            <Box sx={changeRowSx}>
              <EventOutlinedIcon sx={{ fontSize: typography.fontSize[14], color: palette.neutral[470] }} />
              <span>{flightWhen(f.dates.from)}</span>
              <ArrowForwardIcon sx={{ fontSize: typography.fontSize[14], color: "primary.main" }} />
              <Box component="span" sx={{ fontWeight: typography.fontWeight.semiBold, color: palette.neutral[870] }}>
                {flightWhen(f.dates.to)}
              </Box>
            </Box>
          )}
          {asksCabin(f) && (
            <Box sx={{ ...changeRowSx, mt: asksDate(f) ? 0.5 : 0 }}>
              <AirlineSeatReclineNormalOutlinedIcon sx={{ fontSize: typography.fontSize[14], color: palette.neutral[470] }} />
              <span>{cabinLabel(f.cabin.from?.cabinClass) || "Current cabin"}</span>
              <ArrowForwardIcon sx={{ fontSize: typography.fontSize[14], color: "primary.main" }} />
              <Box component="span" sx={{ fontWeight: typography.fontWeight.semiBold, color: palette.neutral[870] }}>
                {cabinLabel(f.cabin.to?.cabinClass) || "—"}
              </Box>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

/** The legs a cancellation request asks to cancel: route · flight, date, PNR. */
export function CancelFlights({ flights, booking }) {
  return (
    <Box sx={{ display: "grid", gap: 1, columnGap: 3, gridTemplateColumns: { xs: "1fr", sm: legColumns(flights) } }}>
      {(flights || []).map((f, i) => (
        <Box key={`${f.sectorId || "flight"}-${i}`} sx={legCellSx(i)}>
          <Typography
            sx={{
              fontSize: typography.fontSize[13],
              fontWeight: typography.fontWeight.semiBold,
              color: palette.neutral[870],
              mb: 0.25,
            }}
          >
            {legTitle(f, bookedLeg(booking, f))}
          </Typography>
          {bookedLeg(booking, f) ? (
            <BookedLeg leg={bookedLeg(booking, f)} />
          ) : (
          <Box sx={changeRowSx}>
            <EventOutlinedIcon sx={{ fontSize: typography.fontSize[14], color: palette.neutral[470] }} />
            <span>{[fmtYmd(f.date || f.dates?.from?.date), f.airline, f.pnr && `PNR ${f.pnr}`].filter(Boolean).join(" · ")}</span>
          </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

/** What a request is about, by kind: date / cabin changes, or legs to cancel.
 *  With the `booking`, each leg also shows how it is booked, and the travellers. */
export function FlightRequestSubject({ request, booking }) {
  return (
    <>
      {booking && <Travellers booking={booking} />}
      {kindOf(request) === "cancel" ? (
        <CancelFlights flights={request?.flights} booking={booking} />
      ) : (
        <RequestChanges flights={request?.flights} booking={booking} />
      )}
    </>
  );
}

/* ── Table helpers ───────────────────────────────────────────────────── */

/** "DEL → AGR" / "DEL → AGR + 1 more" — the flights a request is about. */
export const manualFlightsLabel = (request) => {
  const routes = (request?.flights || []).map((f) => f.route || "Flight");
  if (!routes.length) return "Flight";
  return routes.length === 1 ? routes[0] : `${routes[0]} + ${routes.length - 1} more`;
};

/** "Date change · Cabin change" — what a request asks to change ("Cancellation"
 *  for a cancellation request). */
export const manualChangeSummary = (request) => {
  if (kindOf(request) === "cancel") return "Cancellation";
  const flights = request?.flights || [];
  const parts = [];
  if (flights.some(asksDate)) parts.push("Date change");
  if (flights.some(asksCabin)) parts.push("Cabin change");
  return parts.join(" · ") || "Modification";
};
